import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  ANALYTICS_CONSENT_CHANGED_EVENT,
  loadStoredConsent,
  saveStoredConsent,
  type StoredConsent,
} from '@/lib/consent-storage'

declare global {
  interface Window {
    dataLayer?: unknown[]
    openConsent?: () => void
    gtag?: (...args: unknown[]) => void
  }
}

function pushGtag(args: unknown[]) {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(args)
}

function applyConsent(consent: StoredConsent) {
  pushGtag([
    'consent',
    'update',
    {
      ad_storage: consent.ads ? 'granted' : 'denied',
      ad_user_data: consent.ads ? 'granted' : 'denied',
      ad_personalization: consent.ads ? 'granted' : 'denied',
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      personalization_storage: consent.analytics ? 'granted' : 'denied',
    },
  ])
}

export function ConsentBanner() {
  const [ready, setReady] = useState(false)
  const [open, setOpen] = useState(false)
  const [analytics, setAnalytics] = useState(false)
  const [ads, setAds] = useState(false)
  const [customizeOpen, setCustomizeOpen] = useState(false)

  useEffect(() => {
    const stored = loadStoredConsent()
    // Client-only: read localStorage after mount so prerendered HTML has no dead buttons.
    /* eslint-disable react-hooks/set-state-in-effect -- mount sync from localStorage */
    setAnalytics(stored?.analytics ?? false)
    setAds(stored?.ads ?? false)
    setOpen(stored === null)
    setReady(true)
    /* eslint-enable react-hooks/set-state-in-effect */

    const reopen = () => {
      const current = loadStoredConsent()
      setAnalytics(current?.analytics ?? false)
      setAds(current?.ads ?? false)
      setOpen(true)
    }
    window.addEventListener('consent:open', reopen)
    window.openConsent = reopen

    return () => {
      window.removeEventListener('consent:open', reopen)
      delete window.openConsent
    }
  }, [])

  function persist(consent: StoredConsent) {
    saveStoredConsent(consent)
    applyConsent(consent)
    window.dispatchEvent(new Event(ANALYTICS_CONSENT_CHANGED_EVENT))
    setCustomizeOpen(false)
    setOpen(false)
  }

  function acceptAll() {
    persist({ analytics: true, ads: true, ts: Date.now() })
  }

  function rejectAll() {
    persist({ analytics: false, ads: false, ts: Date.now() })
  }

  function savePreferences() {
    persist({ analytics, ads, ts: Date.now() })
  }

  if (!ready || !open) return null

  return (
    <div
      role="dialog"
      aria-label="Cookies and analytics"
      className="fixed bottom-4 left-4 right-4 z-[var(--z-consent-banner)] animate-fade-up sm:left-6 sm:right-auto sm:max-w-[440px]"
    >
      <Card className="rounded-2xl p-5 shadow-lg">
        <h3 className="text-base font-semibold text-foreground">
          Cookies & Analytics
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          We use cookies for analytics and to improve our site. You can accept
          all, reject non-essential, or customize your choices.{' '}
          <a href="/legal/privacy" className="text-primary underline-offset-4 hover:underline">
            Privacy policy
          </a>
          .
        </p>

        <div className="mt-4 flex flex-nowrap items-center gap-1.5">
          <Button size="sm" className="h-8 px-2.5 text-xs" onClick={acceptAll}>
            Accept All
          </Button>
          <Button size="sm" variant="secondary" className="h-8 px-2.5 text-xs" onClick={rejectAll}>
            Reject Non-Essential
          </Button>
          <Popover open={customizeOpen} onOpenChange={setCustomizeOpen}>
            <PopoverTrigger asChild>
              <Button size="sm" variant="ghost" className="h-8 px-2 text-xs">
                Customize
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              sideOffset={10}
              className="z-[var(--z-consent-popover)] w-[360px] max-w-[calc(100vw-2rem)]"
            >
              <div className="space-y-3">
                <ConsentRow
                  id="consent-essential"
                  title="Essential"
                  description="Required for the site to function. Always on."
                  checked
                  disabled
                  onCheckedChange={() => {}}
                />
                <ConsentRow
                  id="consent-analytics"
                  title="Analytics"
                  description="Helps us understand traffic (Google Analytics)."
                  checked={analytics}
                  onCheckedChange={setAnalytics}
                />
                <ConsentRow
                  id="consent-marketing"
                  title="Marketing"
                  description="Personalized advertising and remarketing."
                  checked={ads}
                  onCheckedChange={setAds}
                />
              </div>
              <Button onClick={savePreferences} className="mt-4 w-full">
                Save Preferences
              </Button>
            </PopoverContent>
          </Popover>
        </div>
      </Card>
    </div>
  )
}

function ConsentRow({
  id,
  title,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string
  title: string
  description: string
  checked: boolean
  disabled?: boolean
  onCheckedChange: (value: boolean) => void
}) {
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border border-border p-3 ${
        disabled ? 'opacity-60' : ''
      }`}
    >
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        className="mt-0.5"
      />
      <div className="min-w-0 space-y-1">
        <Label htmlFor={id} className={disabled ? 'cursor-not-allowed' : ''}>
          {title}
        </Label>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
    </div>
  )
}
